import { useTranslation } from "react-i18next";
import { Pressable } from "react-native";
import { Text } from "react-native-paper";
import type { IconType } from "../../icon";
import type { ScoreRange } from "../../types";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import Button, { type ButtonProps } from "../Button";
import CardHeaderAvatar from "../CardHeaderAvatar";
import { FlatCard } from "../Cards/FlatCard";
import { CompletionRing, type CompletionRingProps } from "../CompletionRing";
import DonutScore from "../DonutScore";
import type { TaskChip } from "../HomeApplet/types";
import { InfoChip } from "../InfoChip";

const buttonConfig: Record<
	string,
	{
		buttonMode: NonNullable<ButtonProps["mode"]>;
		buttonIcon: IconType;
	}
> = {
	NOT_STARTED: {
		buttonMode: "contained",
		buttonIcon: "play-circle-outline",
	},
	ACTIVE: {
		buttonMode: "outlined",
		buttonIcon: "play-pause",
	},
	COMPLETE: {
		buttonMode: "text",
		buttonIcon: "chart-box-outline",
	},
	LEGACYREPORT: {
		buttonMode: "contained-tonal",
		buttonIcon: "chart-box-outline",
	},
	MIGRATEDREPORT: {
		buttonMode: "contained-tonal",
		buttonIcon: "chart-box-outline",
	},
};

type Props = {
	label: string;
	chips?: TaskChip[];
	icon?: string;
	status: string;
	cta: string;
	handleNavigation: () => void;
	locked?: boolean;
	loading?: boolean;
	score: number;
	scoreText?: string;
	scoreRanges?: ScoreRange[];
	completionRing?: CompletionRingProps;
};

export const PracticeTestListItem = ({
	label: title,
	chips,
	icon,
	status,
	cta,
	handleNavigation,
	locked = false,
	loading,
	score,
	scoreText,
	scoreRanges,
	completionRing,
}: Props) => {
	const { isMobile } = useDevice();
	const theme = useAppTheme();
	const { t } = useTranslation();

	const { buttonMode, buttonIcon } =
		buttonConfig[status] ?? buttonConfig.NOT_STARTED;

	const renderLeading = () => {
		if (completionRing) {
			return <CompletionRing {...completionRing} />;
		}
		if (icon) {
			return <CardHeaderAvatar icon={icon} />;
		}
		return (
			<DonutScore
				scoreRanges={scoreRanges}
				distribution={[score, 100 - score]}
				score={score}
				customChildren={
					scoreText ? <Text variant="bodySmall">{scoreText}</Text> : undefined
				}
				variant="progress"
				radius={20}
				innerRadius={16}
			/>
		);
	};

	const renderLeft = () => (
		<Box width={40} height={40}>
			{renderLeading()}
		</Box>
	);

	const renderRight = () =>
		locked ? (
			<Button
				mode="contained-tonal"
				icon={isMobile ? undefined : "lock-open-outline"}
				buttonColor={theme.colors.tertiaryContainer}
				textColor={theme.colors.onTertiaryContainer}
				onPress={handleNavigation}
			>
				{t("common.unlock")}
			</Button>
		) : (
			<Button
				mode={buttonMode}
				icon={buttonIcon}
				loading={loading}
				disabled={loading}
				onPress={handleNavigation}
			>
				{cta}
			</Button>
		);

	const renderTitle = () => <Text variant="titleMedium">{title}</Text>;

	const renderChips = () =>
		chips?.length ? (
			<Box flexDirection="row" flexWrap="wrap" gap={4}>
				{chips.map((chip, index) => (
					<InfoChip
						key={`${chip.icon}-${chip.label}`}
						icon={chip.icon}
						label={chip.iconOnly ? undefined : chip.label}
						// The item-count chip shares its row with the CTA on mobile, so it
						// drops its glyph to leave room for the label.
						hideIcon={isMobile && index === 0 && !chip.iconOnly}
					/>
				))}
			</Box>
		) : null;

	if (isMobile) {
		return (
			<Pressable
				onPress={handleNavigation}
				disabled={loading}
				style={{
					backgroundColor: theme.colors.surface,
					padding: 16,
					gap: 8,
					borderBottomWidth: 1,
					borderBottomColor: theme.colors.surfaceVariant,
				}}
			>
				<Box flexDirection="row" alignItems="center" gap={16}>
					{renderLeft()}
					<Box flex={1} minWidth={0}>
						{renderTitle()}
					</Box>
				</Box>
				<Box
					flexDirection="row"
					alignItems="flex-end"
					justifyContent="space-between"
					gap={8}
				>
					<Box flex={1} minWidth={0} paddingBottom={6}>
						{renderChips()}
					</Box>
					{renderRight()}
				</Box>
			</Pressable>
		);
	}

	return (
		<FlatCard
			onPress={handleNavigation}
			style={{
				overflow: "hidden",
			}}
		>
			<Box
				flexDirection="row"
				alignItems="center"
				justifyContent="space-between"
				gap={16}
				padding={16}
				minHeight={72}
			>
				{renderLeft()}
				<Box flex={1} minWidth={0} gap={8} justifyContent="center">
					{renderTitle()}
					{renderChips()}
				</Box>
				{renderRight()}
			</Box>
		</FlatCard>
	);
};

export default PracticeTestListItem;
